/**
 * WebSocket Hook for Real-time Notifications
 * Uses STOMP over SockJS for reliable messaging.
 *
 * Reconnection strategy:
 *   - On WebSocket failure: exponential backoff up to maxReconnectAttempts
 *   - After exhausting retries: falls back to HTTP polling via notificationService
 *   - HTTP polling interval is configurable (default: 30 s)
 *
 * Connection status values:
 *   'connecting'   - Attempting initial or reconnect connection
 *   'connected'    - STOMP/WebSocket is live
 *   'polling'      - Fallback HTTP polling is active (WebSocket unavailable)
 *   'disconnected' - No active connection and polling is not running
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import environmentConfig from '../config/environment';
import { getNotifications } from '../services/notificationService';

// WebSocket server URL — uses runtime environment detection (not build-time env vars)
const WS_URL =
  environmentConfig.API_URL ||
  environmentConfig.api?.baseURL ||
  'http://localhost:8080';

/**
 * Connection status constants — use these for display logic.
 */
export const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  POLLING: 'polling',
  DISCONNECTED: 'disconnected',
};

/**
 * Hook for managing WebSocket connection with STOMP protocol.
 * Falls back to HTTP polling after maxReconnectAttempts failures.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onNotification - Callback when a notification is received
 * @param {Function} options.onConnect - Callback when WebSocket connects
 * @param {Function} options.onDisconnect - Callback when WebSocket disconnects
 * @param {boolean} options.enabled - Whether to enable the connection (default true)
 * @param {number} options.maxReconnectAttempts - Attempts before falling back to polling (default 5)
 * @param {number} options.pollingIntervalMs - HTTP poll interval in ms (default 30000)
 * @returns {Object} WebSocket state and controls
 */
export const useWebSocket = ({
  onNotification,
  onConnect,
  onDisconnect,
  enabled = true,
  maxReconnectAttempts = 5,
  pollingIntervalMs = 30_000,
} = {}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // ── State ──────────────────────────────────────────────────────────────────
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTION_STATUS.DISCONNECTED
  );
  const [connectionError, setConnectionError] = useState(null);

  // Derived booleans for convenience
  const isConnected = connectionStatus === CONNECTION_STATUS.CONNECTED;
  const isPolling = connectionStatus === CONNECTION_STATUS.POLLING;

  // ── Refs ───────────────────────────────────────────────────────────────────
  const clientRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const lastConnectCallRef = useRef(0);
  const isFallenBackRef = useRef(false);

  // Stable callback refs — avoids triggering re-connect effects when callbacks change
  const onNotificationRef = useRef(onNotification);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);

  useEffect(() => {
    onNotificationRef.current = onNotification;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
  }, [onNotification, onConnect, onDisconnect]);

  // ── HTTP Polling Fallback ─────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Start polling the notifications REST endpoint as a fallback.
   * Passes received notifications through the same onNotification callback
   * so consumers don't need to handle the two modes differently.
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // already polling

    isFallenBackRef.current = true;
    setConnectionStatus(CONNECTION_STATUS.POLLING);
    setConnectionError(null);

    const poll = async () => {
      try {
        const result = await getNotifications(0, 20);
        const notifications = result?.content || result?.data || [];
        notifications.forEach((n) => onNotificationRef.current?.(n));
      } catch {
        // Silently swallow poll errors — connection status stays 'polling'
      }
    };

    // Run immediately then on interval
    poll();
    pollingIntervalRef.current = setInterval(poll, pollingIntervalMs);
  }, [pollingIntervalMs]);

  // ── WebSocket / STOMP ─────────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (clientRef.current) {
      clientRef.current.deactivate();
      clientRef.current = null;
    }
    isConnectingRef.current = false;
    if (!isFallenBackRef.current) {
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    }
  }, []);

  const connect = useCallback(async () => {
    // Debounce rapid calls (1 second minimum between attempts)
    const now = Date.now();
    if (now - lastConnectCallRef.current < 1000) return;
    lastConnectCallRef.current = now;

    if (isConnectingRef.current) return;
    if (!isAuthenticated || !enabled) return;
    if (clientRef.current?.connected) return;
    // Do not attempt WebSocket if already in polling mode
    if (isFallenBackRef.current) return;

    isConnectingRef.current = true;
    setConnectionStatus(CONNECTION_STATUS.CONNECTING);

    try {
      const token = await getAccessTokenSilently();

      const client = new Client({
        webSocketFactory: () => new SockJS(`${WS_URL}/ws`),
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },
        debug: () => {
          // Disabled to reduce console noise.
          // Enable for debugging: console.log('[STOMP]', str);
        },
        // STOMP-level reconnect delay — acts as a second layer of backoff
        reconnectDelay: 5000,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,

        onConnect: () => {
          isConnectingRef.current = false;
          reconnectAttemptsRef.current = 0;
          setConnectionStatus(CONNECTION_STATUS.CONNECTED);
          setConnectionError(null);

          // Subscribe to user-specific notification queue
          client.subscribe('/user/queue/notifications', (message) => {
            try {
              const notification = JSON.parse(message.body);
              onNotificationRef.current?.(notification);
            } catch {
              // Malformed message — ignore
            }
          });

          // Subscribe to read-status updates
          client.subscribe('/user/queue/notifications/read-status', () => {
            // Could be used to sync read status across browser tabs
          });

          // Subscribe to all-read confirmation
          client.subscribe('/user/queue/notifications/all-read', () => {
            // Could trigger a local state sync
          });

          // Subscribe to system-wide broadcast notifications
          client.subscribe('/topic/notifications', (message) => {
            try {
              const notification = JSON.parse(message.body);
              onNotificationRef.current?.(notification);
            } catch {
              // Malformed message — ignore
            }
          });

          // Confirm subscription to the server
          client.publish({
            destination: '/app/notifications/subscribe',
            body: JSON.stringify({ timestamp: Date.now() }),
          });

          onConnectRef.current?.();
        },

        onDisconnect: () => {
          isConnectingRef.current = false;
          if (!isFallenBackRef.current) {
            setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
          }
          onDisconnectRef.current?.();
        },

        onStompError: (frame) => {
          isConnectingRef.current = false;
          setConnectionError(
            frame.headers?.message || 'STOMP connection error'
          );
        },

        onWebSocketError: () => {
          isConnectingRef.current = false;
          setConnectionError('WebSocket connection failed');
          setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);

          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            // Exponential backoff: 2s, 4s, 8s, 16s, 32s — capped at 30s
            const delay = Math.min(
              1000 * Math.pow(2, reconnectAttemptsRef.current),
              30_000
            );
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else {
            // Exhausted WebSocket retries — fall back to HTTP polling
            startPolling();
          }
        },
      });

      clientRef.current = client;
      client.activate();
    } catch (error) {
      isConnectingRef.current = false;
      setConnectionError(error.message);
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    }
  }, [
    isAuthenticated,
    enabled,
    getAccessTokenSilently,
    maxReconnectAttempts,
    startPolling,
  ]);

  // ── Send helpers ──────────────────────────────────────────────────────────

  const sendMessage = useCallback((destination, body) => {
    if (clientRef.current?.connected) {
      clientRef.current.publish({
        destination,
        body: typeof body === 'string' ? body : JSON.stringify(body),
      });
    }
  }, []);

  const acknowledgeNotification = useCallback(
    (notificationId) => {
      sendMessage('/app/notification/ack', { notificationId });
    },
    [sendMessage]
  );

  const sendPing = useCallback(() => {
    sendMessage('/app/ping', { timestamp: Date.now() });
  }, [sendMessage]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Connect on mount and whenever authentication/enabled changes
  useEffect(() => {
    if (isAuthenticated && enabled) {
      connect();
    }

    return () => {
      stopPolling();
      disconnect();
      isFallenBackRef.current = false;
      reconnectAttemptsRef.current = 0;
    };
  }, [isAuthenticated, enabled, connect, disconnect, stopPolling]);

  // Periodic ping to keep the WebSocket connection alive
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      sendPing();
    }, 30_000);

    return () => clearInterval(pingInterval);
  }, [isConnected, sendPing]);

  // ── Public API ────────────────────────────────────────────────────────────

  return {
    /**
     * Granular connection status: 'connecting' | 'connected' | 'polling' | 'disconnected'
     */
    connectionStatus,
    /** true when STOMP WebSocket is live */
    isConnected,
    /** true when falling back to HTTP polling */
    isPolling,
    /** Last connection error message, or null */
    connectionError,
    /** Manually trigger a connection attempt */
    connect,
    /** Tear down connection and polling */
    disconnect,
    sendMessage,
    acknowledgeNotification,
    sendPing,
  };
};

export default useWebSocket;
